import React, { useEffect, useRef, useState } from "react";
import { Send, Mic, ImagePlus, Volume2, Phone, Video, Bot, User, X, MicOff, RefreshCw, ScanLine, Copy, PhoneOff } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

const SLASH = ["/fan off", "/fan auto", "/light on", "/cam snap", "/pir on"];

function aiReply(text, t) {
  const q = text.toLowerCase().trim();
  if (q.startsWith("/")) return `✓ Command executed: ${q} — published to ESP32 over MQTT.`;
  if (q.includes("temp")) return t ? `Current enclosure temperature is ${t.temperature.toFixed(1)}°C. The cooling fan is running at ${t.fan_speed}%. All within safe limits.` : "Temperature data isn't available yet — waiting for live MQTT telemetry.";
  if (q.includes("power") || q.includes("energy")) return t ? `Total real-time power draw across all three phases is ${Math.round(t.total_power)} W with an average power factor of ${t.power_factor}. Cumulative energy is logged to Grafana Cloud.` : "Power data isn't available yet.";
  if (q.includes("phase")) return t ? `L1: ${Math.round(t.phases.L1.power)}W · L2: ${Math.round(t.phases.L2.power)}W · L3: ${Math.round(t.phases.L3.power)}W. Load is reasonably balanced.` : "Phase data pending.";
  if (q.includes("gas") || q.includes("air") || q.includes("quality") || q.includes("ppm")) return t ? `Air quality is currently ${t.gas_quality ?? "--"} — MQ-135 reads ${t.gas_ppm != null ? Math.round(t.gas_ppm) + " ppm" : "no data"}. ${t.gas_quality === "DANGER" ? "DANGER level — ventilate the area immediately!" : t.gas_quality === "POOR" ? "Air quality is poor, consider ventilation." : "Air is within safe limits."}` : "Gas sensor data isn't available yet — waiting for live MQTT telemetry.";
  if (q.includes("hello") || q.includes("hi")) return "Hello! I'm your Campus Energy AI assistant. Ask me about temperature, power, phases, air quality, or use slash commands like /fan auto.";
  return "I'm the Campus Energy assistant. I can report sensor readings (temperature, power, air quality), control devices via slash commands, and explain your 3-phase metering data. Try asking about temperature, power, or air quality.";
}

function Bubble({ m }) {
  const isAI = m.role === "ai";
  return (
    <div className={`flex gap-2.5 ${isAI ? "" : "flex-row-reverse"}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isAI ? "bg-[var(--accent-soft)] text-[var(--accent)]" : "bg-[var(--surface2)] text-[var(--t2)]"}`}>
        {isAI ? <Bot size={16} /> : <User size={16} />}
      </div>
      <div className={`max-w-[75%] px-4 py-2.5 rounded-lg text-sm leading-relaxed ${isAI ? "bg-[var(--surface2)] border border-[var(--bd)]" : "bg-[var(--accent)] text-black"}`}>
        {m.img && <img src={m.img} alt="attach" className="rounded-md mb-2 max-h-40" />}
        {m.text}
      </div>
    </div>
  );
}

function CallTimer({ active }) {
  const [s, setS] = useState(0);
  useEffect(() => { if (!active) return; const id = setInterval(() => setS((x) => x + 1), 1000); return () => clearInterval(id); }, [active]);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return <span className="font-mono text-sm" data-testid="call-timer">{mm}:{ss}</span>;
}

export default function AiChat() {
  const { telemetry, settings } = useApp();
  const [messages, setMessages] = useState([{ role: "ai", text: "Welcome to the Campus Energy AI assistant. Ask about live readings or use slash commands below." }]);
  const [input, setInput] = useState("");
  const [voiceCall, setVoiceCall] = useState(false);
  const [videoCall, setVideoCall] = useState(false);
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("user");

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const speak = (text) => {
    if (settings.voiceEngine === "browser" && window.speechSynthesis) {
      const u = new SpeechSynthesisUtterance(text); u.rate = settings.speechRate; window.speechSynthesis.speak(u);
    } else { toast.info(`TTS (${settings.voiceEngine}) would speak this response`); }
  };

  const send = (txt) => {
    const text = (txt ?? input).trim();
    if (!text) return;
    const userMsg = { role: "user", text };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setTimeout(() => {
      const reply = aiReply(text, telemetry);
      setMessages((m) => [...m, { role: "ai", text: reply }]);
    }, 500);
  };

  const onMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.info("Speech recognition not supported — simulating input"); send("What is the current temperature?"); return; }
    const rec = new SR(); rec.lang = "en-US";
    rec.onresult = (e) => send(e.results[0][0].transcript);
    rec.onerror = () => toast.error("Mic error");
    rec.start(); toast.info("Listening…");
  };

  const onAttach = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => { setMessages((m) => [...m, { role: "user", text: "Analyze this image", img: r.result }, { role: "ai", text: "I can see the attached image. In production I'd run Gemini Vision to analyze the equipment or meter reading shown." }]); };
    r.readAsDataURL(f);
  };

  const startVideo = async (mode = facing) => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { toast.error("Camera access denied"); }
  };

  const openVideo = () => { setVideoCall(true); setTranscript([{ who: "AI", text: "Video call connected. Point the camera at any equipment." }]); setTimeout(() => startVideo(), 300); };
  const closeVideo = () => { setVideoCall(false); if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  const flip = () => { const next = facing === "user" ? "environment" : "user"; setFacing(next); startVideo(next); };
  const analyze = () => setTranscript((t) => [...t, { who: "AI", text: "Frame analyzed: detected a wall-mounted distribution panel. Estimated load looks nominal." }]);

  const openVoice = () => { setVoiceCall(true); setTranscript([{ who: "AI", text: "Voice channel open. How can I help with your energy system?" }]); };

  return (
    <div className="space-y-4">
      <div>
        <Overline>Conversational AI</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Energy Assistant</h2>
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="ai-voice-call" onClick={openVoice} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors"><Phone size={15} className="text-[var(--accent)]" /> Voice Call</button>
        <button data-testid="ai-video-call" onClick={openVideo} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors"><Video size={15} className="text-[var(--accent)]" /> Video Call</button>
      </div>

      <Panel className="flex flex-col h-[520px]">
        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-chat-window">
          {messages.map((m, i) => <Bubble key={i} m={m} />)}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[var(--bd)] p-3">
          <div className="flex items-center gap-2">
            <button data-testid="ai-mic" onClick={onMic} className="w-10 h-10 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors shrink-0"><Mic size={17} /></button>
            <button data-testid="ai-attach" onClick={() => fileRef.current?.click()} className="w-10 h-10 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors shrink-0"><ImagePlus size={17} /></button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAttach} />
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about energy, temperature, or type a /command…"
              className="flex-1 bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors"
            />
            <button data-testid="ai-speak" onClick={() => speak(messages[messages.length - 1]?.text || "")} className="w-10 h-10 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors shrink-0"><Volume2 size={17} /></button>
            <button data-testid="ai-send" onClick={() => send()} className="w-10 h-10 rounded-md bg-[var(--accent)] text-black flex items-center justify-center active:scale-95 transition-transform shrink-0"><Send size={17} /></button>
          </div>
          <div className="mt-2 text-xs text-[var(--t2)] font-mono flex flex-wrap gap-x-3 gap-y-1" data-testid="slash-hints">
            <span className="text-[var(--t2)]">Slash:</span>
            {SLASH.map((c) => <button key={c} onClick={() => send(c)} className="text-[var(--accent)] hover:underline">{c}</button>)}
          </div>
        </div>
      </Panel>

      {/* VOICE CALL OVERLAY */}
      {voiceCall && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-6" data-testid="voice-call-overlay">
          <div className="absolute top-6 left-0 right-0 flex justify-center"><span className="px-3 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)] font-mono text-xs flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[var(--accent)] pulse-dot" /> CONNECTED · <CallTimer active /></span></div>
          <div className="w-32 h-32 rounded-full bg-[var(--accent)]/10 border-2 border-[var(--accent)] flex items-center justify-center mb-6"><Bot size={56} className="text-[var(--accent)]" /></div>
          <div className="text-white font-heading font-bold text-xl mb-1">Energy AI</div>
          <div className="text-[var(--accent)] font-mono text-xs mb-6">{muted ? "MUTED" : "LISTENING…"}</div>
          <div className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-lg p-4 h-40 overflow-y-auto font-mono text-sm text-green-400 mb-8" data-testid="voice-transcript">
            {transcript.map((t, i) => <div key={i} className="mb-1.5"><span className="text-[var(--t2)]">{t.who}:</span> {t.text}</div>)}
          </div>
          <div className="flex gap-4">
            <button data-testid="voice-mute" onClick={() => setMuted((m) => !m)} className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors">{muted ? <MicOff size={20} /> : <Mic size={20} />}</button>
            <button data-testid="voice-end" onClick={() => setVoiceCall(false)} className="w-14 h-14 rounded-full bg-[var(--danger)] flex items-center justify-center text-white"><PhoneOff size={20} /></button>
            <button data-testid="voice-copy" onClick={() => { navigator.clipboard?.writeText(transcript.map((t) => `${t.who}: ${t.text}`).join("\n")); toast.success("Transcript copied"); }} className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"><Copy size={20} /></button>
          </div>
        </div>
      )}

      {/* VIDEO CALL OVERLAY */}
      {videoCall && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="video-call-overlay">
          <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
          <div className="relative z-10 flex justify-between items-center p-5">
            <span className="px-3 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)] font-mono text-xs flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[var(--accent)] pulse-dot" /> LIVE · <CallTimer active /></span>
            <span className="font-mono text-xs text-white/70" data-testid="video-status">Gemini Vision · standby</span>
          </div>
          <div className="relative z-10 mt-auto p-5">
            <div className="max-w-md bg-black/60 border border-white/10 rounded-lg p-3 h-28 overflow-y-auto font-mono text-xs text-green-400 mb-4" data-testid="video-transcript">
              {transcript.map((t, i) => <div key={i} className="mb-1"><span className="text-white/40">{t.who}:</span> {t.text}</div>)}
            </div>
            <div className="flex gap-3 flex-wrap">
              <button data-testid="video-mute" onClick={() => setMuted((m) => !m)} className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10">{muted ? <MicOff size={18} /> : <Mic size={18} />}</button>
              <button data-testid="video-flip" onClick={flip} className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10"><RefreshCw size={18} /></button>
              <button data-testid="video-analyze" onClick={analyze} className="px-4 h-12 rounded-full bg-[var(--accent)] text-black font-medium text-sm flex items-center gap-2"><ScanLine size={18} /> Analyze Frame</button>
              <button data-testid="video-copy" onClick={() => { navigator.clipboard?.writeText(transcript.map((t) => `${t.who}: ${t.text}`).join("\n")); toast.success("Copied"); }} className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10"><Copy size={18} /></button>
              <button data-testid="video-end" onClick={closeVideo} className="w-12 h-12 rounded-full bg-[var(--danger)] flex items-center justify-center text-white"><X size={18} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
